/**
 * Email Dynamic Variable Selector Component
 *
 * Dropdown selector for dynamic restaurant URL variables.
 * These variables resolve to customer's preferred restaurant at send time.
 *
 * Adapted from SMS DynamicVariableSelector for email format.
 *
 * Phase 1, Task 1.3
 * Date: 2025-10-21
 */

import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Variable, Info } from 'lucide-react';
import { EMAIL_VARIABLES } from './types';
import type { EmailVariable } from './types';
import { hasVariable } from '@/utils/email/variableHelpers';
import { cn } from '@/lib/utils';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface EmailDynamicVariableSelectorProps {
  /** Callback when variable is selected */
  onVariableSelect: (variable: string) => void;

  /** Disabled state */
  disabled?: boolean;

  /** Render as list instead of dropdown (for use inside modals) */
  renderAsList?: boolean;

  /** Current email content to check which variables are active */
  currentEmailContent?: string;

  /** Restaurants to show example URLs from */
  restaurants?: RestaurantDetails[];
}

const EmailDynamicVariableSelector: React.FC<EmailDynamicVariableSelectorProps> = ({
  onVariableSelect,
  disabled = false,
  renderAsList = false,
  currentEmailContent = '',
  restaurants = []
}) => {
  const [open, setOpen] = useState(false);

  const handleVariableSelect = (variable: string) => {
    onVariableSelect(variable);
    setOpen(false);
  };

  // Get dynamic variables from the EMAIL_VARIABLES constant
  const dynamicVariables = EMAIL_VARIABLES.dynamic;

  // Group variables by category for display
  const groupedVariables = dynamicVariables.reduce((acc, variable) => {
    const category = 'Restaurant Links'; // All dynamic vars are restaurant links
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, EmailVariable[]>);

  // Check if variable is in current email
  const isVariableActive = (variable: string): boolean => {
    return hasVariable(currentEmailContent, variable);
  };

  // Map variable names to restaurant URL fields
  const urlFieldMap: Record<string, keyof RestaurantDetails> = {
    '{{preferredRestaurantOrderingUrl}}': 'ordering_page_url',
    '{{preferredRestaurantReviewUrl}}': 'review_page_url',
    '{{preferredRestaurantGoogleReviewUrl}}': 'google_review_url',
    '{{preferredRestaurantFacebookUrl}}': 'facebook_url',
    '{{preferredRestaurantInstagramUrl}}': 'instagram_url'
  };

  // Check if URL is available for this variable
  const isUrlAvailable = (variable: EmailVariable): boolean => {
    if (!restaurants || restaurants.length === 0) return false;

    const firstRestaurant = restaurants[0];
    const urlField = urlFieldMap[variable.variable];

    if (!urlField) return false;

    const url = firstRestaurant[urlField];
    return !!url && url.trim() !== '';
  };

  // Get example URL from first restaurant (only if available)
  const getExampleUrl = (variable: EmailVariable): string | undefined => {
    if (!restaurants || restaurants.length === 0) return undefined;

    const firstRestaurant = restaurants[0];
    const urlField = urlFieldMap[variable.variable];

    if (!urlField) return undefined;

    const restaurantUrl = firstRestaurant[urlField];

    // Only return if URL exists and is not empty
    return restaurantUrl && restaurantUrl.trim() !== '' ? restaurantUrl : undefined;
  };

  // Render as simple list when inside modal (to avoid nested overlays)
  if (renderAsList) {
    return (
      <div className="space-y-2">
        {dynamicVariables.map((variable) => {
          const Icon = variable.icon;
          const isActive = isVariableActive(variable.variable);
          const exampleUrl = getExampleUrl(variable);
          const urlAvailable = isUrlAvailable(variable);

          return (
            <Button
              key={variable.variable}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => handleVariableSelect(variable.variable)}
              disabled={disabled || !urlAvailable}
              className={cn(
                "w-full justify-start text-left h-auto py-3",
                isActive && "!text-white [&_*]:!text-white",
                !urlAvailable && "opacity-50 cursor-not-allowed"
              )}
            >
              <div className="flex items-start gap-3 w-full">
                <div className="mt-0.5">
                  {Icon && <Icon className={cn("h-4 w-4", isActive && "!text-white !stroke-white")} />}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-sm font-medium">
                      {variable.variable}
                    </code>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-xs",
                        isActive && "bg-white/20 !text-white border-white/30"
                      )}
                    >
                      Dynamic
                    </Badge>
                  </div>
                  <p className="text-xs text-left">
                    {variable.description}
                  </p>
                  {exampleUrl ? (
                    <p className="text-xs italic text-left">
                      Example: {exampleUrl}
                    </p>
                  ) : (
                    <p className="text-xs italic text-left text-muted-foreground">
                      No URL configured for this restaurant
                    </p>
                  )}
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    );
  }

  // Default: Render as dropdown button
  return (
    <TooltipProvider>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="gap-2"
          >
            <Variable className="h-4 w-4" />
            Insert Dynamic URL
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-96">
          <DropdownMenuLabel className="flex items-center justify-between">
            Dynamic Restaurant URLs
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="text-sm">
                  Dynamic variables automatically resolve to each customer's preferred
                  restaurant at send time. The system uses a 3-tier fallback:
                  <br />
                  1. Customer's last restaurant
                  <br />
                  2. Organization defaults
                  <br />
                  3. Campaign's restaurant
                </p>
              </TooltipContent>
            </Tooltip>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {Object.entries(groupedVariables).map(([category, variables]) => (
            <div key={category}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {category}
              </DropdownMenuLabel>
              {variables.map((variable) => {
                const Icon = variable.icon;
                const exampleUrl = getExampleUrl(variable);
                const urlAvailable = isUrlAvailable(variable);

                return (
                  <DropdownMenuItem
                    key={variable.variable}
                    onClick={() => urlAvailable && handleVariableSelect(variable.variable)}
                    disabled={!urlAvailable}
                    className={cn(
                      "cursor-pointer",
                      !urlAvailable && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="mt-0.5">
                        {Icon && <Icon className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-medium">
                            {variable.variable}
                          </code>
                          <Badge variant="secondary" className="text-xs">
                            Dynamic
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {variable.description}
                        </p>
                        {exampleUrl ? (
                          <p className="text-xs text-muted-foreground italic">
                            Example: {exampleUrl}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">
                            No URL configured for this restaurant
                          </p>
                        )}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </div>
          ))}

          <DropdownMenuSeparator />
          <div className="p-2">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> These URLs automatically resolve to the
              customer's preferred restaurant based on their order history.
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};

export default EmailDynamicVariableSelector;
